/**
 * The one control language the whole builder uses: a row of pressed/unpressed
 * chips. Radio semantics, but sized for a wet thumb rather than a mouse.
 */

interface ChipGroupProps<T extends string | number> {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
  /** When set and different from `value`, shows a "Recommended: X" control
   *  next to the label that reverts to it in one click. This field can be
   *  set automatically elsewhere (depth or subject changing), so an override
   *  needs to stay visible rather than blend in with a deliberate choice. */
  recommendedValue?: T;
  /** Same role as `TextField`'s hint: context a diver needs before picking,
   *  not after — shown every time, not just on error. */
  hint?: string;
}

export function ChipGroup<T extends string | number>({
  label,
  value,
  options,
  onChange,
  recommendedValue,
  hint,
}: ChipGroupProps<T>) {
  const recommended =
    recommendedValue !== undefined && recommendedValue !== value
      ? options.find((o) => o.value === recommendedValue)
      : undefined;

  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <span className="font-display text-[13px] font-semibold uppercase tracking-[0.16em] text-graphite-2">
          {label}
        </span>
        {recommended && (
          <button
            type="button"
            onClick={() => onChange(recommended.value)}
            className="cursor-pointer font-display text-[11px] font-semibold uppercase tracking-[0.05em] text-smb underline decoration-dotted underline-offset-2 hover:text-graphite"
          >
            Recommended: {recommended.label}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-[5px]" role="group" aria-label={label}>
        {options.map((opt) => {
          const pressed = opt.value === value;
          return (
            <button
              key={String(opt.value)}
              type="button"
              aria-pressed={pressed}
              onClick={() => onChange(opt.value)}
              className={[
                "cursor-pointer rounded-[2px] border-[1.5px] border-graphite px-[11px] py-[5px]",
                "font-display text-[15px] font-semibold uppercase tracking-[0.05em] transition-colors",
                pressed
                  ? "bg-graphite text-paper"
                  : "bg-transparent text-graphite hover:bg-graphite/10",
              ].join(" ")}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {hint && <p className="mt-1 text-[11.5px] leading-snug text-graphite-2">{hint}</p>}
    </div>
  );
}
